import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  COLORS,
  Modal,
  Text,
  TextButton,
  UnstyledButton,
} from '@clutter/clean';

import { SharedCheckoutData } from '@root/components/checkout/data';
import { SharedCheckoutStepProps } from '@root/components/checkout/types';
import { XButton } from '@root/components/icons/x_button';
import { Errors } from '@root/resources/errors';
import { PHONE_MASK } from '@root/resources/masks';
import { checkoutValidations } from '@root/resources/validations/checkout';
import { validate } from '@root/resources/validator';

import { FieldText } from '@shared/field_text';

import { useIsomorphicBreakpoints } from '@utils/hooks/use_breakpoints';

import {
  CheckoutModal,
  ModalCloseButton,
  ModalContainer,
  ModalTitle,
  PillButton,
  SwipeableBottomSheet,
} from '../steps/subcomponents/modal_shared';

export const EditContactModal: React.FC<{
  isOpen: boolean;
  values: Pick<SharedCheckoutData, 'name' | 'phone' | 'email'>;
  onChange: SharedCheckoutStepProps['onChange'];
  onClose(): void;
  /** Multi-page final checkout — match `CheckoutModal` / `ModalContainer` sidebar pattern */
  visualVariant?: 'default' | 'funnelCheckout';
}> = ({ isOpen, values, onChange, onClose, visualVariant = 'default' }) => {
  const { isMobile } = useIsomorphicBreakpoints();
  const [leadValues, setLeadValues] = useState(values);
  const [errors, setErrors] = useState(() => new Errors());
  useEffect(() => {
    if (isOpen) {
      setLeadValues(values);
    }
  }, [isOpen, setLeadValues, values]);

  const updateField = <K extends keyof SharedCheckoutData>(
    key: K,
    value: SharedCheckoutData[K],
  ) => {
    const nextValue = { ...leadValues, [key]: value };
    setErrors(
      validate(nextValue, checkoutValidations, ['name', 'phone', 'email']),
    );
    setLeadValues(nextValue);
  };

  const submit = () => {
    const nextErrors = validate(leadValues, checkoutValidations, [
      'name',
      'phone',
      'email',
    ]);
    if (nextErrors.any) {
      setErrors(nextErrors);
      return;
    }
    onChange('name', leadValues.name);
    onChange('phone', leadValues.phone);
    onChange('email', leadValues.email);
    onChange('leadToken', undefined);
    onClose();
  };

  if (visualVariant === 'funnelCheckout') {
    const modalContentWithoutClose = (
      <Box.Flex flexDirection="column" gap="18px">
        <ModalTitle>Contact details</ModalTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Box.Flex flexDirection="column" gap="18px">
            <FieldText
              label="First & Last name"
              name="name"
              autoComplete="name"
              error={errors.messages.name}
              onChange={updateField}
              value={leadValues.name}
            />
            <FieldText
              label="Phone number"
              name="phone"
              type="tel"
              autoComplete="phone"
              error={errors.messages.phone}
              mask={PHONE_MASK}
              onChange={updateField}
              value={leadValues.phone}
            />
            <FieldText
              label="Email address"
              name="email"
              autoComplete="email"
              error={errors.messages.email}
              onChange={updateField}
              value={leadValues.email}
            />
            <PillButton fullWidth type="submit" disabled={errors.any}>
              Update details
            </PillButton>
          </Box.Flex>
        </form>
      </Box.Flex>
    );

    if (isMobile) {
      if (!isOpen) return null;
      return (
        <>
          <div
            role="presentation"
            aria-hidden="true"
            tabIndex={-1}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
            onClick={onClose}
          />
          <SwipeableBottomSheet onClose={onClose}>
            {modalContentWithoutClose}
          </SwipeableBottomSheet>
        </>
      );
    }

    return (
      <CheckoutModal isOpen={isOpen} onClose={onClose}>
        <ModalContainer>
          <ModalCloseButton onClose={onClose} />
          {modalContentWithoutClose}
        </ModalContainer>
      </CheckoutModal>
    );
  }

  return (
    <Modal isOpen={isOpen} handleModalClose={onClose}>
      <Box
        minWidth={[null, '568px', '652px']}
        width={['calc(100vw - 32px)', 'initial']}
        padding="24px"
        position="relative"
      >
        <Box position="absolute" top="28px" right="24px">
          <UnstyledButton onClick={onClose}>
            <XButton />
          </UnstyledButton>
        </Box>
        <Box
          textAlign="center"
          margin="0 -24px 16px"
          padding="0 0 16px"
          borderColor={COLORS.grayBorder}
          borderStyle={'none none solid none'}
        >
          <Text.Title size="extraSmall">Edit Contact</Text.Title>
        </Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <FieldText
            label="Full Name"
            name="name"
            autoComplete="name"
            error={errors.messages.name}
            onChange={updateField}
            value={leadValues.name}
          />
          <FieldText
            label="Email"
            name="email"
            autoComplete="email"
            error={errors.messages.email}
            onChange={updateField}
            value={leadValues.email}
          />
          <FieldText
            label="Phone number"
            name="phone"
            type="tel"
            autoComplete="phone"
            error={errors.messages.phone}
            mask={PHONE_MASK}
            onChange={updateField}
            value={leadValues.phone}
          />
          <Button fullWidth type="submit" disabled={errors.any}>
            Save contact details
          </Button>
          <Box textAlign="center" padding="16px 16px 0">
            <TextButton
              size="medium"
              onClick={(e) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </TextButton>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
